import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  Textarea,
  useIsMobile,
} from "@nfp-tracker/components";
import { SUPPORT_EMAIL, SUPPORT_MAILTO_URL, cn } from "@nfp-tracker/utils";
import type { Ticket } from "posthog-js";
import { ChevronLeft, Loader2, MessageSquarePlus, Send } from "lucide-react";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSupportChat, type SupportChatState } from "../hooks/useSupportChat";
import { MessageContent } from "./MessageContent";
import { SupportChatContext } from "./SupportChatContext";

/** Treat the reader as "following" the conversation within this distance of the bottom. */
const NEAR_BOTTOM_PX = 80;
/** Composer grows from one line to this, then scrolls internally. */
const COMPOSER_MAX_PX = 128;

const formatTimestamp = (isoTimestamp: string) => {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const formatTicketTimestamp = (isoTimestamp: string | undefined) => {
  if (!isoTimestamp) return "";

  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) return "";

  const isToday = parsed.toDateString() === new Date().toDateString();
  return isToday
    ? parsed.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const TicketList = ({
  tickets,
  isLoading,
  onSelect,
}: {
  tickets: Ticket[];
  isLoading: boolean;
  onSelect: (ticketId: string) => void;
}) => {
  if (isLoading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No conversations yet.
      </p>
    );
  }

  return (
    <ul className="flex min-w-0 flex-col divide-y">
      {tickets.map((ticket) => (
        <li key={ticket.id} className="min-w-0">
          <button
            type="button"
            onClick={() => onSelect(ticket.id)}
            className="flex w-full min-w-0 flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-muted/60"
          >
            <div className="flex w-full min-w-0 items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {ticket.last_message || "New conversation"}
              </span>
              <span className="shrink-0 text-[10px] text-muted-foreground">
                {formatTicketTimestamp(ticket.last_message_at || ticket.created_at)}
              </span>
            </div>
            <div className="flex w-full items-center gap-2">
              <span className="text-xs capitalize text-muted-foreground">
                {ticket.status.replace(/_/g, " ")}
              </span>
              {(ticket.unread_count ?? 0) > 0 && (
                <span className="rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-medium text-destructive-foreground">
                  {ticket.unread_count} new
                </span>
              )}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};

const SupportChatPanel = ({
  isOpen,
  onOpenChange,
  chat,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  chat: SupportChatState;
}) => {
  const isMobile = useIsMobile();
  const {
    status,
    tickets,
    selectedTicketId,
    selectTicket,
    startNewConversation,
    canReplyToSelection,
    messages,
    isLoadingMessages,
    isLoadingTickets,
    isSending,
    sendError,
    sendMessage,
    unreadAnchorCount,
  } = chat;
  const [view, setView] = useState<"list" | "thread">("thread");
  const [draft, setDraft] = useState("");
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef(new Map<string, HTMLDivElement>());
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const hasAnchoredThreadRef = useRef(false);

  const isThreadView = view === "thread";

  const getViewport = useCallback(
    () =>
      scrollRootRef.current?.querySelector<HTMLElement>(
        "[data-radix-scroll-area-viewport]"
      ) ?? null,
    []
  );

  const scrollToBottom = useCallback(() => {
    const viewport = getViewport();
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [getViewport]);

  // Re-anchor whenever the panel opens or the thread changes, so reopening lands on the
  // latest content rather than wherever the previous thread was left.
  useEffect(() => {
    hasAnchoredThreadRef.current = false;
  }, [isOpen, selectedTicketId, view]);

  useLayoutEffect(() => {
    if (!isOpen || !isThreadView || messages.length === 0) return;

    const viewport = getViewport();
    if (!viewport) return;

    const isFirstPass = !hasAnchoredThreadRef.current;
    const distanceFromBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;

    // Once anchored, only keep following if the reader hasn't scrolled up into history —
    // yanking them back down mid-read would be worse than a missed new message.
    if (!isFirstPass && distanceFromBottom > NEAR_BOTTOM_PX) return;

    hasAnchoredThreadRef.current = true;

    // Unread replies are the trailing messages, so counting back from the end gives the
    // first one the user hasn't seen. Anything else just goes to the bottom.
    const firstUnread =
      isFirstPass && unreadAnchorCount > 0 && unreadAnchorCount < messages.length
        ? messages[messages.length - unreadAnchorCount]
        : null;
    const anchorElement = firstUnread ? messageRefs.current.get(firstUnread.id) : null;

    if (anchorElement) {
      anchorElement.scrollIntoView({ block: "start" });
    } else {
      scrollToBottom();
    }
  }, [
    isOpen,
    isThreadView,
    messages,
    unreadAnchorCount,
    getViewport,
    scrollToBottom,
  ]);

  // Images in a reply finish loading after layout, which moves the bottom out from under a
  // reader who was pinned to it.
  useEffect(() => {
    if (!isOpen || !isThreadView) return;

    const viewport = getViewport();
    const content = viewport?.firstElementChild;
    if (!viewport || !content) return;

    const observer = new ResizeObserver(() => {
      const distanceFromBottom =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      if (distanceFromBottom <= NEAR_BOTTOM_PX) scrollToBottom();
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, [isOpen, isThreadView, getViewport, scrollToBottom, messages.length]);

  useEffect(() => {
    if (!isOpen) setDraft("");
  }, [isOpen]);

  // Starts at one line and grows with the content, rather than reserving the textarea's
  // default multi-line height up front.
  useLayoutEffect(() => {
    const element = composerRef.current;
    if (!element) return;

    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, COMPOSER_MAX_PX)}px`;
  }, [draft, isThreadView, isOpen]);

  const handleSend = useCallback(async () => {
    if (!draft.trim() || isSending) return;

    const sent = await sendMessage(draft);
    if (sent) {
      setDraft("");
      setView("thread");
    }
  }, [draft, isSending, sendMessage]);

  const transcript = useMemo(
    () =>
      messages.map((message) => {
        const isCustomer = message.author_type === "customer";

        return (
          <div
            key={message.id}
            ref={(element) => {
              if (element) messageRefs.current.set(message.id, element);
              else messageRefs.current.delete(message.id);
            }}
            className={cn("flex w-full min-w-0", isCustomer ? "justify-end" : "justify-start")}
          >
            <div
              className={cn(
                "min-w-0 max-w-[85%] rounded-2xl px-3 py-2 text-sm",
                isCustomer
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground"
              )}
            >
              {!isCustomer && message.author_name && (
                <p className="mb-0.5 text-xs font-medium opacity-70">{message.author_name}</p>
              )}
              <MessageContent message={message} />
              <p className="mt-1 text-right text-[10px] opacity-60">
                {formatTimestamp(message.created_at)}
              </p>
            </div>
          </div>
        );
      }),
    [messages]
  );

  const headerTitle = isThreadView
    ? selectedTicketId
      ? "Support"
      : "New conversation"
    : "Conversations";

  const canGoBackToList = isThreadView && tickets.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex flex-col gap-0 overflow-hidden p-0",
          "duration-300 ease-out",
          "data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100",
          "data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 data-[state=open]:slide-in-from-bottom-6",
          "data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0 data-[state=closed]:slide-out-to-bottom-6",
          // Full screen on phones (with the notch/home-indicator insets respected), a
          // corner panel on anything larger.
          "left-0 top-0 h-[100dvh] max-h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0",
          "sm:left-auto sm:top-auto sm:bottom-6 sm:right-6 sm:h-[600px] sm:max-h-[calc(100dvh-3rem)] sm:w-[400px] sm:max-w-[calc(100vw-3rem)] sm:rounded-xl sm:border"
        )}
        closeButtonClassName="top-[calc(1rem+env(safe-area-inset-top,0px))] right-[calc(1rem+env(safe-area-inset-right,0px))] sm:top-4 sm:right-4"
        style={
          isMobile
            ? {
                paddingTop: "env(safe-area-inset-top, 0px)",
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
                paddingLeft: "env(safe-area-inset-left, 0px)",
                paddingRight: "env(safe-area-inset-right, 0px)",
              }
            : undefined
        }
      >
        <DialogHeader className="border-b px-4 py-3 text-left">
          <div className="flex items-center gap-2 pr-6">
            {canGoBackToList && (
              <button
                type="button"
                aria-label="Back to conversations"
                onClick={() => setView("list")}
                className="-ml-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <DialogTitle className="min-w-0 flex-1 truncate text-base">
              {headerTitle}
            </DialogTitle>
            {status === "ready" && (
              <button
                type="button"
                aria-label="Start a new conversation"
                onClick={() => {
                  startNewConversation();
                  setView("thread");
                }}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <MessageSquarePlus className="h-4 w-4" />
              </button>
            )}
          </div>
          <DialogDescription className="text-xs">
            {status === "unavailable"
              ? `Chat is unavailable right now — email us at ${SUPPORT_EMAIL}.`
              : "We'll reply here, usually within one business day. Feel free to provide feedback, feature requests, bug reports, or ask for assistance."}
          </DialogDescription>
        </DialogHeader>

        {status === "unavailable" ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              We couldn't start a chat session. Email works just as well.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                window.location.href = SUPPORT_MAILTO_URL;
              }}
            >
              Email {SUPPORT_EMAIL}
            </Button>
          </div>
        ) : (
          <>
            {/*
              Radix's ScrollArea wraps children in a `display: table` element, which sizes to
              its content — a long unbroken string then widens the transcript past the
              viewport and scrolls the horizontal padding out of view with it. Forcing that
              wrapper to a block is what keeps the panel vertical-scroll-only.
            */}
            <ScrollArea
              ref={scrollRootRef}
              className="min-h-0 w-full flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block"
            >
              {isThreadView ? (
                <div className="flex min-w-0 flex-col gap-3 px-4 py-4">
                  {status === "checking" || (isLoadingMessages && messages.length === 0) ? (
                    <div className="flex items-center justify-center py-8 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  ) : messages.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      Send us a message and we'll get back to you here.
                    </p>
                  ) : (
                    transcript
                  )}
                </div>
              ) : (
                <TicketList
                  tickets={tickets}
                  isLoading={isLoadingTickets}
                  onSelect={(ticketId) => {
                    selectTicket(ticketId);
                    setView("thread");
                  }}
                />
              )}
            </ScrollArea>

            {isThreadView && (
              <div className="border-t p-3">
                {sendError && (
                  <p className="mb-2 text-xs text-destructive" role="alert">
                    {sendError}
                  </p>
                )}
                {canReplyToSelection ? (
                  <div className="flex items-center gap-2">
                    <Textarea
                      ref={composerRef}
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      onKeyDown={(event) => {
                        // Enter sends; Shift+Enter keeps the newline, since the box is a
                        // textarea so longer bug reports stay readable.
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          void handleSend();
                        }
                      }}
                      placeholder="How can we help?"
                      rows={1}
                      className="min-h-10 resize-none overflow-y-auto py-2 text-base sm:text-sm leading-5"
                      disabled={status !== "ready" || isSending}
                    />
                    <Button
                      size="icon"
                      className="shrink-0"
                      aria-label="Send message"
                      onClick={() => void handleSend()}
                      disabled={status !== "ready" || isSending || !draft.trim()}
                    >
                      {isSending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ) : (
                  // PostHog's SDK can only post to the ticket it considers current, so
                  // replying here would land the message on a different thread. Read-only,
                  // with the header's new-conversation action as the way forward.
                  <p className="text-center text-xs text-muted-foreground">
                    This conversation is closed — start a new one to keep talking.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Owns the support chat for the whole app shell. Chat state lives here rather than in the
 * panel so the unread count survives the panel being closed — that's what badges the sidebar
 * entry point. The panel's DOM is still only mounted once it has been opened.
 */
export const SupportChatProvider = ({ children }: PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const chat = useSupportChat(isOpen);

  const value = useMemo(
    () => ({
      isOpen,
      openSupportChat: () => {
        setHasOpened(true);
        setIsOpen(true);
      },
      closeSupportChat: () => setIsOpen(false),
      unreadCount: chat.unreadCount,
    }),
    [isOpen, chat.unreadCount]
  );

  return (
    <SupportChatContext.Provider value={value}>
      {children}
      {hasOpened && <SupportChatPanel isOpen={isOpen} onOpenChange={setIsOpen} chat={chat} />}
    </SupportChatContext.Provider>
  );
};
